import { Component, ComponentRef, Injector, OnChanges, OnDestroy, OnInit, ViewChild, ViewContainerRef, createNgModule } from "@angular/core";
import { AuthService, GlobalsService, LoginService, SleepService } from "@services";
import { IUserInfoModel, IRmaClaimModel } from "@models";
import { SlickDialogComponent } from "@slick-components";
import { Subscription } from "rxjs";
import { SignalRMessageStore, TextChatStore } from '@stores';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ThemeService } from "@components/theme.service";
import { ThemeStore } from "@stores";


@Component({
	selector: "side-menu",
	templateUrl: "side-menu.component.html",
	styleUrls: ['side-menu.component.scss']
})

export class SideMenuComponent implements OnInit, OnDestroy {
	pendingOrdersCount$: Subscription;
	suspendedOrdersCount$: Subscription;
	warrantiesOrdersCount$: Subscription;
	smartlistOverdueCount$: Subscription;
	textChatUnreadMessageCount$: Subscription;
	recurringBillingErrrorCount$: Subscription;
	skeditPayRecurringPaymentsErrorCount$: Subscription;
	jellyFishUnapprovedOrdersCount$: Subscription;
	rmaNewClaimsCount$: Subscription;
	themeChangedSubscription$: Subscription;

	// In-app "New Submission" claim dialog. The submit form is created on demand into
	// newSubmissionHost and destroyed on close, so every submission starts clean and the form's
	// code stays out of the eagerly loaded layout bundle (see openNewSubmission).
	@ViewChild("newSubmissionDialog") newSubmissionDialog: SlickDialogComponent;
	@ViewChild("newSubmissionHost", { read: ViewContainerRef }) newSubmissionHost: ViewContainerRef;
	showNewSubmission: boolean = false;
	private newSubmissionRef: ComponentRef<any>;
	private newSubmissionSubmitted$: Subscription;

	firstName: string;
	profilePictureUrl: string;
	user: IUserInfoModel;
	userIsClockedIn: boolean;
	companyName: string;
	highlightCustomers: boolean;
	highlightJobs: boolean;
	highlightAccounting: boolean;
	highlightInventory: boolean;
	highlightPurchasing: boolean;
	highlightReports: boolean;
	highlightSettings: boolean;
	highlightClaims: boolean;
	customersExpanded: boolean;
	jobsExpanded: boolean;
	accountingExpanded: boolean;
	inventoryExpanded: boolean;
	purchasingExpanded: boolean;
	reportsExpanded: boolean;
	settingsExpanded: boolean;
	claimsExpanded: boolean;
	get isSettingsOpen(): boolean {
		return GlobalsService.isSettingsOpen;
	}

	isSkeditPayAdmin = GlobalsService.userInfo.roleTypeId === 1;

	isMenuExpanded: boolean = (localStorage.getItem("MENU_EXPANDED") || 'true') === 'true';

	canAccessSchedule: boolean = false;
	canAccessCustomers: boolean = false;
	canAccessCRM: boolean = false;
	canAccessSalesPipeline: boolean = false;
	canAccessCustomerMerge: boolean = false;
	canAccessJobs: boolean = false;
	canAccessRecurringJobs: boolean = false
	canAccessLathamOrders: boolean = false;
	canAccessLathamBillBatch: boolean = false;
	canAccessPartsList: boolean = false;
	canAccessInventory: boolean = false;
	canAccessEstimates: boolean = false;
	canAccessSalesOrders: boolean = false;
	canAccessInvoices: boolean = false;
	canAccessPOS: boolean = false;
	canAccessPayments: boolean = false;
	canAccessTextChat: boolean = false;
	canAccessEmailLog: boolean = false;
	canAccessDocuments: boolean = false;
	canAccessSmartlist: boolean = false;
	canAccessReports: boolean = false;
	canAccessTimeclock: boolean = false;
	canAccessSettings: boolean = false;
	canAccessQuickbooks: boolean = false;
	canAccessUsers: boolean = false;
	canAccessVendors: boolean = false;
	canAccessPurchaseOrders: boolean = false;
	canAccessReceiving: boolean = false;
	canAccessPickList: boolean = false;
	canAccessShipping: boolean = false;
	canAccessClaimsCenter: boolean = false;
	canAccessRmaTracker: boolean = false;

	pendingOrdersCount: number = 0;
	suspendedOrdersCount: number = 0;
	warrantiesOrdersCount: number = 0;
	totalOrdersCount: number = 0;
	totalJellyFishOrdersCount: number = 0;
	smartlistOverdueCount: number = 0;
	textChatUnreadMessagesCount: number = 0;
	recurringBillingErrorCount: number = 0;
	skeditPayRecurringPaymentsErrorCount: number = 0;
	jellyFishUnapprovedOrdersCount: number = 0;
	rmaNewClaimsCount: number = 0;
	logoWhite: boolean;

	isIMPC = (GlobalsService.company.companyId === 1);
	isMizu = (GlobalsService.company.companyId === 1);
	isCanada = (GlobalsService.company.companyId === 3);
	isQPro = GlobalsService.company.isQPro;
	isJellyFish = (GlobalsService.company.companyId === 133)
	isLathamOrdersOnly = GlobalsService.company.isLathamOrdersOnly;
	mizuOrdering = true;
	skeditPay: boolean = GlobalsService.company.useSkeditPay;
	isQuickbooksEnabled: boolean = GlobalsService.isQuickbooksEnabled;
	useJellyFishOrdering: boolean = GlobalsService.company.useJellyFishOrdering;
	isJellyFishTrial: boolean = GlobalsService.company.isJellyFishTrial;
	isPavinTheWay: boolean = GlobalsService.company.isPavinTheWay;
	useQuoteTracking: boolean = GlobalsService.company.useQuoteTracking;
	sideMenusExpanded: string[] = JSON.parse(localStorage.getItem("SUB_MENUS_EXPANDED")) || [];
	get showJobsNotifications(): boolean {
		return GlobalsService.userInfo?.layoutSettings?.showJobsNotifications ?? true;
	}

	get showAccountingNotifications(): boolean {
		return GlobalsService.userInfo?.layoutSettings?.showAccountingNotifications ?? true;
	}

	get showLathamOrdersNotifications(): boolean {
		return GlobalsService.userInfo?.layoutSettings?.showLathamOrdersNotifications ?? true;
	}

	get showTextChatNotifications(): boolean {
		return GlobalsService.userInfo?.layoutSettings?.showTextChatNotifications ?? true;
	}

	constructor(private readonly authService: AuthService,
		private readonly loginService: LoginService,
		private signalRMessageStore: SignalRMessageStore,
		private themeStore: ThemeStore,
		private router: Router,
		private location: Location,
		private injector: Injector,
		private textChatStore: TextChatStore) {

        this.canAccessSchedule = GlobalsService.checkPermission("schedule", "access");
        this.canAccessCustomers = GlobalsService.checkPermission("customers", "access");
        this.canAccessCRM = GlobalsService.checkPermission("crm", "access");
        this.canAccessSalesPipeline = GlobalsService.checkPermission("SalesPipeline", "Access");
		this.canAccessCustomerMerge = GlobalsService.checkPermission("customerMerge", "access");
        this.canAccessJobs = GlobalsService.checkPermission("jobs", "access");
        this.canAccessRecurringJobs = GlobalsService.checkPermission("RecurringJobs", "access");
        this.canAccessLathamOrders = GlobalsService.company.useLathamOrdering && GlobalsService.checkPermission("latham orders", "access");
        this.canAccessLathamBillBatch = GlobalsService.userInfo.userId === 2078 || GlobalsService.userInfo.userId === 20;
        this.canAccessPartsList = GlobalsService.checkPermission("parts list", "access");
        this.canAccessInventory = GlobalsService.company.useInventory && GlobalsService.checkPermission("inventory", "access");
        this.canAccessEstimates = GlobalsService.checkPermission("estimates", "access");
        this.canAccessSalesOrders = GlobalsService.checkPermission("SalesOrders", "access");
        this.canAccessInvoices = GlobalsService.checkPermission("invoices", "access");
        this.canAccessPOS = GlobalsService.canAccessPos();
        this.canAccessPayments = GlobalsService.checkPermission("payments", "access");
        this.canAccessPickList = GlobalsService.company.useInventory && GlobalsService.checkPermission("Inventory", "AccessPickList");
        this.canAccessTextChat = (GlobalsService.company.useTextChat && GlobalsService.checkPermission("textchat", "access"));
		this.canAccessEmailLog = GlobalsService.checkPermission("emailLog", "access");
        this.canAccessDocuments = GlobalsService.checkPermission("documents", "access");
        this.canAccessSmartlist = GlobalsService.checkPermission("smartlist", "access");
        this.canAccessReports = GlobalsService.checkPermission("reports", "access");
        this.canAccessVendors = GlobalsService.company.useInventory && GlobalsService.checkPermission("vendors", "access");
        this.canAccessPurchaseOrders = GlobalsService.company.useInventory && GlobalsService.checkPermission("PurchaseOrders", "access");
        this.canAccessReceiving = GlobalsService.company.useInventory && GlobalsService.checkPermission("receiving", "access");
        this.canAccessTimeclock = GlobalsService.checkPermission("timeclock", "access");
        this.canAccessSettings = GlobalsService.checkPermission("settings", "access");
        this.canAccessQuickbooks = GlobalsService.company.quickbooksWebConnectorEnabled && GlobalsService.checkPermission("quickbooks", "access");
        this.canAccessUsers = GlobalsService.checkPermission("users", "access");
        this.canAccessShipping = GlobalsService.company.useShipping && GlobalsService.checkPermission("shipping", "access");
        this.canAccessClaimsCenter = GlobalsService.checkPermission("ClaimsCenter", "access");
        this.canAccessRmaTracker = GlobalsService.checkPermission("RmaTracker", "access");

		this.firstName = GlobalsService.userInfo.firstName;
		this.profilePictureUrl = GlobalsService.userInfo.profilePictureUrl;
		this.companyName = GlobalsService.company.companyName;

		this.user = GlobalsService.userInfo;
	}

	async ngOnInit() {
		this.setLogoColor(GlobalsService.userInfo?.theme || 'Default');

		if (this.authService.isAuthenticated === true && GlobalsService.company.useLathamOrdering) {

			this.pendingOrdersCount$ = this.signalRMessageStore.lathamOrderPendingCount.subscribe(pendingOrdersCount => {
				this.pendingOrdersCount = pendingOrdersCount;
				this.totalOrdersCount = (this.pendingOrdersCount || 0) + (this.suspendedOrdersCount || 0) + (this.warrantiesOrdersCount || 0);
			});

			this.suspendedOrdersCount$ = this.signalRMessageStore.lathamOrderSuspendedCount.subscribe(suspendedOrdersCount => {
				this.suspendedOrdersCount = suspendedOrdersCount;
				this.totalOrdersCount = (this.pendingOrdersCount || 0) + (this.suspendedOrdersCount || 0) + (this.warrantiesOrdersCount || 0);
			});

			this.warrantiesOrdersCount$ = this.signalRMessageStore.lathamOrderWarrantiesCount.subscribe(warrantiesCount => {
				this.warrantiesOrdersCount = warrantiesCount;
				this.totalOrdersCount = (this.pendingOrdersCount || 0) + (this.suspendedOrdersCount || 0) + (this.warrantiesOrdersCount || 0);
			});

		}
		this.recurringBillingErrrorCount$ = this.signalRMessageStore.recurringBillingErrorCount.subscribe(errorCount => {
			this.recurringBillingErrorCount = errorCount;
		});

		this.skeditPayRecurringPaymentsErrorCount$ = this.signalRMessageStore.skeditPayRecurringPaymentsErrorCount.subscribe(errorCount => {
			this.skeditPayRecurringPaymentsErrorCount = errorCount;
		});

		this.jellyFishUnapprovedOrdersCount$ = this.signalRMessageStore.jellyFishUnapprovedOrdersCount.subscribe(unapprovedOrders => {
			this.jellyFishUnapprovedOrdersCount = unapprovedOrders;
		});

		this.rmaNewClaimsCount$ = this.signalRMessageStore.rmaNewClaimsCount.subscribe(count => {
			this.rmaNewClaimsCount = count;
		});



		this.themeChangedSubscription$ = this.themeStore.themeChanged$.subscribe(themeName => {
			this.setLogoColor(themeName);
		});

		const url = this.location.path();
		this.checkHighlight(url)

		this.smartlistOverdueCount$ = this.signalRMessageStore.smartlistOverdueCount.subscribe(smartlistOverdueCount => {
			this.smartlistOverdueCount = smartlistOverdueCount;
		});

		if (GlobalsService.company.useTextChat) {
			this.textChatUnreadMessageCount$ = this.textChatStore.unreadMessagesCount$.subscribe(textChatUnreadMessagesCount => {
				this.textChatUnreadMessagesCount = textChatUnreadMessagesCount;
			});
		}
		if (this.isMenuExpanded)
			document.getElementById("main-area-container").classList.add('menu-is-open');

		if (GlobalsService.isLoggedIn &&
			GlobalsService.company.useTextChat === true &&
			GlobalsService.checkPermission("TextChat", "Access") === true) {
			// Unread messages
		}

		this.sideMenusExpanded.forEach(x => {
			if (x === "Customers") {
				this.customersExpanded = true;
			}
			else if (x === "Jobs") {
				this.jobsExpanded = true;
			}
			else if (x === "Accounting") {
				this.accountingExpanded = true;
			}
			else if (x === "Inventory") {
				this.inventoryExpanded = true;
			}
			else if (x === "Purchasing") {
				this.purchasingExpanded = true;
			}
			else if (x === "Reports") {
				this.reportsExpanded = true;
			}
			else if (x === "Settings") {
				this.settingsExpanded = true;
			}
			else if (x === "Claims") {
				this.claimsExpanded = true;
			}
		});

		if (GlobalsService.company.parentCompanyId == 106 || GlobalsService.company.parentCompanyId == 3 || GlobalsService.company.companyId == 106 || GlobalsService.company.companyId == 3) {
			this.mizuOrdering = false;
		}

	}

	openSettings() {
		const currentUrl = this.router.url;
		sessionStorage.setItem('lastVisitedUrl', currentUrl);
		sessionStorage.setItem('isSettingsOpen', 'true');
		GlobalsService.isSettingsOpen = true;
		this.router.navigate(['/settings']);
	  }

	  closeSettings() {
		GlobalsService.isSettingsOpen = false;
		sessionStorage.removeItem('isSettingsOpen');
		
		// Navigate to the last visited page (outside of the outlet)
		const lastUrl = sessionStorage.getItem('lastVisitedUrl');
		if (lastUrl) {
			this.router.navigateByUrl(lastUrl);
		}
	  }

	resetHighlight() {
		this.highlightCustomers = false;
		this.highlightJobs = false;
		this.highlightAccounting = false;
		this.highlightInventory = false;
		this.highlightPurchasing = false;
		this.highlightReports = false;
		this.highlightSettings = false;
		this.highlightClaims = false;
	}

	toggleLocalStorage(menuOption: string) {
		let menuOptionExists = this.sideMenusExpanded.findIndex(x => x === menuOption);
		if (menuOptionExists >= 0) {
			this.sideMenusExpanded.splice(menuOptionExists, 1);
		}
		else {
			this.sideMenusExpanded.push(menuOption)
		}
		localStorage.setItem("SUB_MENUS_EXPANDED", JSON.stringify(this.sideMenusExpanded));
	}

	checkHighlight(label: string) {
		this.resetHighlight();
		switch (label) {
			case "/customers":
			case "/crm":
			case "/sales-pipeline":
			case "/customer-merge":
				this.highlightCustomers = true;
				break;
			case '/jobs':
			case '/recurring':
			case '/smartlist':
				this.highlightJobs = true;
				break;
			case '/estimates':
			case '/salesOrders':
			case '/invoices':
			case '/lathamOrders/billBatch':
			case '/pos':
			case '/payments':
			case '/quickbooks':
			case '/recurring-billing':
				this.highlightAccounting = true;
				break;
			case '/inventory':
			case '/items':
			case '/pickList':
				this.highlightInventory = true;
				break;
			case '/vendors':
			case '/purchaseOrders':
			case '/receiving':
				this.highlightPurchasing = true;
				break;
			case '/reporting':
			case '/reporting/apptAveragesReport':
			case '/reporting/itemUsageReport':
			case '/reporting/itemUsageByBuilderReport':
			case '/reporting/lathamOrderInsights':
			case '/reporting/resourceApptTypesReport':
			case '/reporting/employeeApptsReport':
			case '/reporting/employeeAvgHoursWorked':
            case '/reporting/powerbiReport':
				this.highlightReports = true;
				break;
			case '/settings':
			case '/users':
				this.highlightSettings = true;
				break;
			case '/claims-center':
			case '/rma-tracker':
				this.highlightClaims = true;
				break;


		}
	}

	toggleExpanded() {
		this.isMenuExpanded = !this.isMenuExpanded;
		document.getElementById("main-area-container").classList.toggle('menu-is-open');
		localStorage.setItem("MENU_EXPANDED", this.isMenuExpanded.toString());
	}

	// Name stamped on claims submitted from the in-app dialog: always the logged-in user, so the
	// submitter is tracked automatically without them typing it. The server separately stamps
	// SubmittedByUserId and (when this user is an account exec) auto-assigns them as the claim's AE.
	get currentUserFullName(): string {
		return GlobalsService.userInfo?.fullName || '';
	}

	async openNewSubmission() {
		// Open the submission form in an in-app dialog (embedded mode) instead of the external
		// mizuclaims site. showDialog() displays the shell; the form itself is fetched and built
		// below so a fresh one is used every time.
		this.showNewSubmission = true;
		this.newSubmissionDialog.showDialog();

		if (this.newSubmissionRef)
			return;

		// The claim form is ~160 KB and is only needed once someone opens this dialog, so it is
		// pulled in on demand rather than being imported by LayoutModule (which is eager and would
		// put it in main.js on every page load). The module is instantiated too because it is what
		// provides RmaClaimsService, which the form injects.
		const { RmaClaimSubmitComponentsModule } = await import("@app/rma-claims/rma-claim-submit-page/rma-claim-submit-components.module");
		const { RmaClaimSubmitPageComponent } = await import("@app/rma-claims/rma-claim-submit-page/rma-claim-submit-page.component");

		// The dialog may have been closed again while the chunk was downloading.
		if (!this.showNewSubmission || !this.newSubmissionHost)
			return;

		const moduleRef = createNgModule(RmaClaimSubmitComponentsModule, this.injector);

		this.newSubmissionHost.clear();
		this.newSubmissionRef = this.newSubmissionHost.createComponent(RmaClaimSubmitPageComponent, { ngModuleRef: moduleRef });
		this.newSubmissionRef.setInput("embedded", true);
		this.newSubmissionRef.setInput("prefillSubmitterName", this.currentUserFullName);
		this.newSubmissionSubmitted$ = this.newSubmissionRef.instance.submitted
			.subscribe((claim: IRmaClaimModel) => this.onNewSubmissionSubmitted(claim));
	}

	onNewSubmissionClosed() {
		// Tear down the embedded form so the next open starts fresh (and its object URLs are revoked).
		this.showNewSubmission = false;
		this.destroyNewSubmission();
	}

	private destroyNewSubmission() {
		if (this.newSubmissionSubmitted$) {
			this.newSubmissionSubmitted$.unsubscribe();
			this.newSubmissionSubmitted$ = null;
		}

		if (this.newSubmissionHost)
			this.newSubmissionHost.clear();

		this.newSubmissionRef = null;
	}

	onNewSubmissionSubmitted(_claim: IRmaClaimModel) {
		// The embedded form shows its own confirmation screen after submitting; nothing to refresh here.
	}

	navigateToReportsPage() {
		this.router.navigate(['/reporting/reports-list']);
	}

	ngOnDestroy() {
		if (this.pendingOrdersCount$)
			this.pendingOrdersCount$.unsubscribe();
		if (this.suspendedOrdersCount$)
			this.suspendedOrdersCount$.unsubscribe();
		if (this.warrantiesOrdersCount$)
			this.warrantiesOrdersCount$.unsubscribe();
		if (this.smartlistOverdueCount$)
			this.smartlistOverdueCount$.unsubscribe();
		if (this.textChatUnreadMessageCount$)
			this.textChatUnreadMessageCount$.unsubscribe();
		if (this.recurringBillingErrrorCount$)
			this.recurringBillingErrrorCount$.unsubscribe();
		if (this.skeditPayRecurringPaymentsErrorCount$)
			this.skeditPayRecurringPaymentsErrorCount$.unsubscribe();
		if (this.rmaNewClaimsCount$)
			this.rmaNewClaimsCount$.unsubscribe();
		if (this.themeChangedSubscription$) {
			this.themeChangedSubscription$.unsubscribe();
		}

		this.destroyNewSubmission();
	}

	private setLogoColor(themeName: string) {
		if (themeName === 'superhero' || themeName === 'slate' || themeName === 'solar') 
			this.logoWhite = true;
		else 
			this.logoWhite = false;		

	}


}